import { Controller } from "@hotwired/stimulus"

// Notification dropdown that works correctly on iPhone Safari.
//
// Root cause of mobile breakage: the panel lives inside a `sticky` header
// that has `backdrop-filter` applied. Backdrop-filter creates a new stacking
// context, which causes `fixed` children to be positioned relative to that
// element rather than the viewport — breaking layout on iOS.
//
// Fix: teleport the panel DOM node to <body> via panelTargetConnected so it
// is never clipped by a transformed/filtered ancestor. Position it with JS
// using the trigger button's getBoundingClientRect().
//
// Using target lifecycle callbacks (panelTargetConnected/Disconnected) instead
// of connect() to avoid "Missing target element" errors when Turbo replaces
// the page and the controller re-initialises before the target is in the DOM.

export default class extends Controller {
  static targets = ["panel"]

  connect() {
    this._onOutside = this._onOutside.bind(this)
    this._boundBeforeCache = this._beforeCache.bind(this)
    document.addEventListener("turbo:before-cache", this._boundBeforeCache)

    if (this.hasPanelTarget) {
      this._panel = this.panelTarget
      this._panel.style.position = "fixed"
      this._panel.style.zIndex   = "9999"
      document.body.appendChild(this._panel)
    } else {
      // Fallback for improperly cached state
      const orphanedPanel = document.querySelector('body > [data-notification-dropdown-target="panel"]')
      if (orphanedPanel) {
        this._panel = orphanedPanel
      }
    }
  }

  disconnect() {
    this._close()
    document.removeEventListener("turbo:before-cache", this._boundBeforeCache)
    this._returnPanel()
  }

  _beforeCache() {
    this._close()
    this._returnPanel()
  }

  _returnPanel() {
    if (this._panel && document.body.contains(this._panel)) {
      this.element.appendChild(this._panel)
    }
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()

    if (!this._panel) return

    const hidden = this._panel.classList.contains("hidden")
    if (hidden) {
      this._position()
      this._panel.classList.remove("hidden")
      requestAnimationFrame(() => {
        document.addEventListener("click",      this._onOutside, { passive: true })
        document.addEventListener("touchstart", this._onOutside, { passive: true })
      })
    } else {
      this._close()
    }
  }

  _close() {
    this._panel?.classList.add("hidden")
    document.removeEventListener("click",      this._onOutside)
    document.removeEventListener("touchstart", this._onOutside)
  }

  _onOutside(event) {
    if (this.element.contains(event.target)) return
    if (this._panel?.contains(event.target))  return
    this._close()
  }

  _position() {
    const rect  = this.element.getBoundingClientRect()
    const panel = this._panel
    const vw    = window.innerWidth

    panel.style.top      = (rect.bottom + 6) + "px"
    panel.style.bottom   = "auto"
    panel.style.right    = (vw - rect.right) + "px"
    panel.style.left     = "auto"
    panel.style.width    = "320px"
    panel.style.maxWidth = "calc(100vw - 32px)"
  }
}
